import React, { useState, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';

export interface SwipeAction {
  icon: React.ReactNode;
  color: string;
  label?: string;
  onClick: () => void;
}

export interface SwipeableCardProps {
  children: React.ReactNode;
  leftAction?: SwipeAction;
  rightAction?: SwipeAction;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  swipeThreshold?: number;
  triggerThreshold?: number;
  disabled?: boolean;
}

const SwipeableCard: React.FC<SwipeableCardProps> = ({
  children,
  leftAction,
  rightAction,
  onSwipeLeft,
  onSwipeRight,
  swipeThreshold = 80,
  triggerThreshold = 150,
  disabled = false,
}) => {
  const [offset, setOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handlers = useSwipeable({
    onSwiping: (eventData) => {
      if (disabled) return;

      const { deltaX } = eventData;

      // Only allow swipe in valid directions
      if (deltaX < 0 && !leftAction) return;
      if (deltaX > 0 && !rightAction) return;

      setIsSwiping(true);

      // Limit max swipe distance
      const maxOffset = triggerThreshold + 30;
      const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, deltaX));
      setOffset(clampedOffset);
    },
    onSwipedLeft: (eventData) => {
      if (disabled || !leftAction) return;

      if (Math.abs(eventData.deltaX) >= triggerThreshold) {
        // Trigger action
        leftAction.onClick();
        onSwipeLeft?.();
      }

      // Reset position
      setOffset(0);
      setIsSwiping(false);
    },
    onSwipedRight: (eventData) => {
      if (disabled || !rightAction) return;

      if (Math.abs(eventData.deltaX) >= triggerThreshold) {
        // Trigger action
        rightAction.onClick();
        onSwipeRight?.();
      }

      // Reset position
      setOffset(0);
      setIsSwiping(false);
    },
    onSwiped: () => {
      setOffset(0);
      setIsSwiping(false);
    },
    trackMouse: false,
    trackTouch: true,
    delta: 10,
    preventScrollOnSwipe: true,
  });

  const showLeftAction = offset < -swipeThreshold && leftAction;
  const showRightAction = offset > swipeThreshold && rightAction;

  const leftActionOpacity = leftAction ? Math.min(1, Math.abs(offset) / swipeThreshold) : 0;
  const rightActionOpacity = rightAction ? Math.min(1, Math.abs(offset) / swipeThreshold) : 0;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        overflow: 'hidden',
        touchAction: 'pan-y',
      }}
      {...handlers}
    >
      {/* Left action background (delete - swipe left reveals this) */}
      {leftAction && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: Math.abs(Math.min(0, offset)),
            backgroundColor: leftAction.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: 20,
            opacity: offset < 0 ? leftActionOpacity : 0,
            transition: isSwiping ? 'none' : 'opacity 0.2s ease',
          }}
        >
          <div style={{ color: 'white', textAlign: 'center' }}>
            <div style={{ fontSize: 24 }}>{leftAction.icon}</div>
            {leftAction.label && (
              <div style={{ fontSize: 12, marginTop: 4 }}>{leftAction.label}</div>
            )}
          </div>
        </div>
      )}

      {/* Right action background (edit - swipe right reveals this) */}
      {rightAction && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: Math.max(0, offset),
            backgroundColor: rightAction.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingLeft: 20,
            opacity: offset > 0 ? rightActionOpacity : 0,
            transition: isSwiping ? 'none' : 'opacity 0.2s ease',
          }}
        >
          <div style={{ color: 'white', textAlign: 'center' }}>
            <div style={{ fontSize: 24 }}>{rightAction.icon}</div>
            {rightAction.label && (
              <div style={{ fontSize: 12, marginTop: 4 }}>{rightAction.label}</div>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <div
        style={{
          transform: `translateX(${offset}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease',
          backgroundColor: 'white',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeableCard;
